// constants
import upArrow from 'static/images/up-arrow.svg';
import downArrow from 'static/images/down-arrow.svg';

// Libraries
import { getId } from '../../utils/helperMethods';
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

// Styles
import './accordion.scss';

const Indicator = ({ isArrowIndicator, isExpanded }) => {
    if (isArrowIndicator) {
        return isExpanded ? (
            <img src={upArrow} alt="^" />
        ) : (
            <img src={downArrow} alt="down" />
        );
    }
    return isExpanded ? '—' : '+';
};

/**
 * Accordion Component
 *
 * @param {Object} props - React component props
 */
const Accordion = props => {
    const {
        accordionData,
        imageUrl,
        accordionToggleHandler,
        isOpened,
        upperComponent,
        lowerComponent,
        customClass,
        id,
        isAlwaysExpendable,
        isArrowIndicator,
    } = props;
    const [isExpanded, setIsExpanded] = useState(isOpened);

    /* Effect is to enable state update from parent. Simply overriding the initial
    state value, won't re-render. Therefore, updating state here */
    useEffect(() => {
        if (accordionToggleHandler) {
            setIsExpanded(isOpened);
        }
    }, [accordionToggleHandler, isOpened]);

    /**
     * Method to toggle accordion state
     *
     * @returns {undefined}
     */
    function toggleAccordion() {
        /* Checking if accordion's open/closed state is being managed by parent.
        In that case, accordionToggleHandler from parent will handle open/close logic */
        if (accordionToggleHandler) {
            accordionToggleHandler(accordionData, isExpanded);
        } else {
            /* When accordion's open/closed state is being managed locally */
            setIsExpanded(!isExpanded);
        }
    }

    return (
        <div
            className={`accordion-container ${customClass ? customClass : ''}`}
            data-state={isExpanded}
        >
            <div
                className="accordion-upper-section"
                id={getId(id)}
                data-testid={getId(id)}
                onClick={toggleAccordion}
            >
                <div className="upper-section-content">{upperComponent}</div>
                {imageUrl ? (
                    <img
                        className={isExpanded ? 'expanded icon' : 'icon'}
                        src={imageUrl}
                        alt="accordion-icon"
                        height="15px"
                        width="15px"
                        title="accordion-icon"
                    />
                ) : (
                    <span className={isExpanded ? 'arrow expanded' : 'arrow'}>
                        {!isAlwaysExpendable ? (
                            <Indicator
                                isArrowIndicator={isArrowIndicator}
                                isExpanded={isExpanded}
                            />
                        ) : (
                            ''
                        )}
                    </span>
                )}
            </div>
            <div
                data-testid={`${getId(id)}-loweSec`}
                className={`accordion-lower-section ${
                    isExpanded ? 'expanded' : ''
                }`}
            >
                <div className="accordion-separator-line" />
                <div className="lower-section-content">{lowerComponent}</div>
            </div>
        </div>
    );
};

Accordion.propTypes = {
    /* Object containing data that is passed to accordion */
    accordionData: PropTypes.object,
    /* Method to toggle open/closed state of accordion */
    accordionToggleHandler: PropTypes.func,
    /* String to add custom class in accordion */
    customClass: PropTypes.string,
    /* String that gets rendered image onto the upper section of accordion */
    imageUrl: PropTypes.string,
    /* Describes whether accordion is expanded or not */
    isOpened: PropTypes.bool,
    /* React component that gets rendered onto the lower section of accordion */
    lowerComponent: PropTypes.element,
    /* React component that gets rendered onto the upper section of accordion */
    upperComponent: PropTypes.element,
    id: PropTypes.string,
    isAlwaysExpendable: PropTypes.bool,
    isArrowIndicator: PropTypes.bool,
};

export default Accordion;
